import { charmsOfTier, charmById } from '../charms/catalog';
import { grantCharm } from '../charms/stake';
import { powerIsFree, powerupById } from '../powerups';
import type { Rng } from '../rng';
import { addTrickStock } from './trickStock';
import { emptyLog } from './hollowLog';
import { pushLedger } from './ledger';
import type { Profile } from './types';

/** The painted page is 1080×1920. The portrait stage is 720×1280, two thirds of that. */
export const SPIN_PAGE_W = 1080;
export const SPIN_PAGE_H = 1920;
export const SPIN_LAYOUT_W = 720;
export const SPIN_LAYOUT_H = 1280;
export const SPIN_PAGE_SCALE = SPIN_LAYOUT_W / SPIN_PAGE_W;

/**
 * geom.txt from the approved page: stand, disc, disc size, spin-button top and height.
 * Button width and the Odds plaque come from page_v3.py.
 */
const GEOM = {
  standX: 13,
  standY: 321,
  discX: 84,
  discY: 490,
  disc: 912,
  buttonY: 1560,
  buttonH: 216,
  buttonW: 700,
  oddsW: 250,
  oddsX: 1080 - 250 - 50,
  oddsY: 1790,
  oddsH: Math.round((256 * 250) / 608),
};

function page(n: number): number {
  return n * SPIN_PAGE_SCALE;
}

export const SPIN_LAYOUT = {
  standX: page(GEOM.standX),
  standY: page(GEOM.standY),
  discX: page(GEOM.discX),
  discY: page(GEOM.discY),
  disc: page(GEOM.disc),
  buttonX: page((SPIN_PAGE_W - GEOM.buttonW) / 2),
  buttonY: page(GEOM.buttonY),
  buttonW: page(GEOM.buttonW),
  buttonH: page(GEOM.buttonH),
  oddsX: page(GEOM.oddsX),
  oddsY: page(GEOM.oddsY),
  oddsW: page(GEOM.oddsW),
  oddsH: page(GEOM.oddsH),
  titleY: page(150),
  subtitleY: page(255),
  titlePx: page(120),
  subtitlePx: page(52),
  buttonLabelPx: page(64),
  oddsLabelPx: page(54),
  titleStroke: page(10),
  subtitleStroke: page(6),
  buttonStroke: page(7),
  oddsStroke: page(5),
} as const;

export function discCenter(): { x: number; y: number; r: number } {
  return {
    x: SPIN_LAYOUT.discX + SPIN_LAYOUT.disc / 2,
    y: SPIN_LAYOUT.discY + SPIN_LAYOUT.disc / 2,
    r: SPIN_LAYOUT.disc / 2,
  };
}

/** Sheen travels for about a second, then rests, on a 1.6s period. Null during the rest. */
export function sheenCenterX(tSec: number, left: number, width: number): number | null {
  const period = 1.6;
  const sweep = 1.05;
  const u = (tSec % period) / sweep;
  if (u > 1) return null;
  const ease = 0.5 - 0.5 * Math.cos(u * Math.PI);
  const pad = page(150);
  return left - pad + (width + pad * 2) * ease;
}

export const SWIPE_MIN_PX = 40;
export const SWIPE_MIN_SPEED = 300;

export const WEDGE_COUNT = 8;
/**
 * One wedge. The painted disc is eight equal slices, clockwise from the top.
 * The gold spokes on disc.png sit on the half-wedge lines (22.5° + k·45°).
 * Slice centers are k·45°. The art and the logical wedges share that zero.
 */
export const WEDGE_DEG = 360 / WEDGE_COUNT;
/**
 * Keep the pointer this far inside the slice, as a fraction of the wedge.
 * 0.2 leaves the inner 60%. The outer band is where a stop looks like the
 * boundary between two prizes.
 */
export const WEDGE_EDGE_INSET = 0.2;
/**
 * A spin uses the middle of that safe band, not the band's edges.
 * 0.5 is a small offset: the central half of the inner 60%.
 */
export const LANDING_JITTER = 0.5;
/**
 * Acorn tip in pointer.png (1055×1177). The painting is placed at its
 * natural size on the 1080×1920 page, so these are page pixels from the
 * image origin. The tip is left of the hub by a fraction of a degree.
 */
const POINTER_TIP_X = 522;
const POINTER_TIP_Y = 181;

/**
 * Clockwise from the top, matching the disc painting.
 * Phaser angles are clockwise-positive. The acorn tip is fixed just left of
 * the hub's vertical, so the rest angle is `-index * 45` plus that tip bias.
 */
export const SPIN_WEDGES = [
  { id: 'acorns-40', index: 0, weight: 30, odds: '40 acorns — 30%' },
  { id: 'tricks', index: 1, weight: 20, odds: '2 Tricks — 20%' },
  { id: 'copper', index: 2, weight: 12, odds: 'Copper Charm — 12%' },
  { id: 'acorns-150', index: 3, weight: 10, odds: '150 acorns — 10%' },
  { id: 'log', index: 4, weight: 10, odds: 'Prize Box, 1 hour sooner — 10%' },
  { id: 'mystery', index: 5, weight: 9, odds: 'Mystery gift — 9%' },
  { id: 'honey', index: 6, weight: 8, odds: '10 honey — 8%' },
  { id: 'crown', index: 7, weight: 1, odds: 'Golden Crown — 1%' },
] as const;

export type SpinWedgeId = (typeof SPIN_WEDGES)[number]['id'];

export const SPIN_ODDS_NOTES = [
  'A Copper Charm is one random Copper Charm.',
  '2 Tricks is two copies of a Trick you own.',
  'A Mystery gift is 80 acorns, 20 honey, or 3 Tricks.',
  'The Golden Crown is a Gold Charm and 500 acorns.',
  'One free spin each day. It returns at midnight.',
] as const;

export function wrap360(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/**
 * Clockwise degrees from straight up to the acorn tip, around the disc center.
 * Negative means the tip sits slightly left of the hub. The angle is the same
 * at any device pixel ratio: the page is scaled as a whole, and rounding the
 * sprite to a pixel moves the tip by a fraction of a degree.
 */
export function pointerTipBiasDeg(): number {
  const tipX = GEOM.standX + POINTER_TIP_X;
  const tipY = GEOM.standY + POINTER_TIP_Y;
  const cx = GEOM.discX + GEOM.disc / 2;
  const cy = GEOM.discY + GEOM.disc / 2;
  return (Math.atan2(tipX - cx, -(tipY - cy)) * 180) / Math.PI;
}

/** Art angle, clockwise from the top of the unrotated disc, now on the hub's vertical. */
export function pointerArtDeg(discDeg: number): number {
  return wrap360(-discDeg);
}

/** Art angle under the acorn tip. That is the slice the player is reading. */
export function pointerTipArtDeg(discDeg: number): number {
  return wrap360(pointerArtDeg(discDeg) + pointerTipBiasDeg());
}

/**
 * Which painted wedge the acorn tip is on.
 * A stop exactly on a boundary counts as the next wedge clockwise.
 */
export function wedgeIndexAtPointer(discDeg: number): number {
  const index = Math.floor((pointerTipArtDeg(discDeg) + WEDGE_DEG / 2) / WEDGE_DEG);
  return ((index % WEDGE_COUNT) + WEDGE_COUNT) % WEDGE_COUNT;
}

/**
 * Where in the safe band a unit random should land.
 * The result is 0..1 across the inner 60%, clustered in the middle of it.
 */
export function landingAlong(unit: number): number {
  const u = Math.min(1, Math.max(0, unit));
  const span = Math.min(1, Math.max(0, LANDING_JITTER));
  return (1 - span) / 2 + u * span;
}

/**
 * Disc angle that parks wedge `index` under the acorn tip.
 * `along` is 0..1 across the safe interior: 0 and 1 sit `WEDGE_EDGE_INSET`
 * in from the edges, and 0.5 is the center. The rest pose is this angle,
 * not whatever the wind-up or overshoot passed through.
 */
export function landingDeg(index: number, along = 0.5): number {
  const t = Math.min(1, Math.max(0, along));
  const u = WEDGE_EDGE_INSET + t * (1 - 2 * WEDGE_EDGE_INSET);
  const art = index * WEDGE_DEG + (u - 0.5) * WEDGE_DEG;
  return wrap360(pointerTipBiasDeg() - art);
}

/** Screen angle of a slice center, clockwise from straight up, at this disc angle. */
export function sliceCenterScreenDeg(index: number, discDeg: number): number {
  return wrap360(index * WEDGE_DEG + discDeg);
}

/** How far the wheel must travel, not counting full extra revolutions. */
export function wedgeOffsetDeg(startDeg: number, index: number, direction: 1 | -1, along = 0.5): number {
  const land = landingDeg(index, along);
  const start = wrap360(startDeg);
  if (direction > 0) return wrap360(land - start);
  return wrap360(start - land);
}

export interface SpinMotion {
  windupSec: number;
  spinSec: number;
  settleSec: number;
  glowSec: number;
  backDeg: number;
  overDeg: number;
  baseTurns: number;
}

/** render_swipe.py spin_fn. Bonus turns add a little time so the wheel does not only speed up. */
export function spinMotion(extraTurns: number, reduced = false): SpinMotion {
  const extra = Math.max(0, extraTurns);
  if (reduced) {
    return { windupSec: 0.2, spinSec: 0.9, settleSec: 0.25, glowSec: 0.6, backDeg: 8, overDeg: 2, baseTurns: 1 };
  }
  return {
    windupSec: 0.4,
    spinSec: 14.4 + extra * 0.45,
    settleSec: 0.45,
    glowSec: 1.5,
    backDeg: 14,
    overDeg: 4,
    baseTurns: 13,
  };
}

export function spinTravelDeg(
  startDeg: number,
  index: number,
  direction: 1 | -1,
  extraTurns: number,
  baseTurns = 13,
  along = 0.5,
): number {
  return wedgeOffsetDeg(startDeg, index, direction, along) + (baseTurns + Math.max(0, extraTurns)) * 360;
}

export function spinTotalSec(motion: SpinMotion): number {
  return motion.windupSec + motion.spinSec + motion.settleSec + motion.glowSec;
}

export type SpinPhase = 'windup' | 'spin' | 'settle' | 'glow' | 'done';

export function spinPhaseAt(t: number, motion: SpinMotion): SpinPhase {
  if (t < motion.windupSec) return 'windup';
  if (t < motion.windupSec + motion.spinSec) return 'spin';
  if (t < motion.windupSec + motion.spinSec + motion.settleSec) return 'settle';
  if (t < spinTotalSec(motion)) return 'glow';
  return 'done';
}

/**
 * Clockwise degrees. Positive `direction` is clockwise on screen.
 * The ease is `1 - (1-u)^3.2`, then a 4° cosine settle, from render_swipe.py.
 */
export function spinAngleDeg(
  t: number,
  start: number,
  direction: 1 | -1,
  turnsDeg: number,
  motion: SpinMotion,
): number {
  const { windupSec, spinSec, settleSec, backDeg, overDeg } = motion;
  if (t <= 0) return start;
  if (t < windupSec) {
    const u = t / windupSec;
    return start - direction * backDeg * Math.sin((u * Math.PI) / 2) ** 2;
  }
  let u = t - windupSec;
  if (u < spinSec) {
    const e = 1 - (1 - u / spinSec) ** 3.2;
    return start + direction * (-backDeg + (turnsDeg + overDeg + backDeg) * e);
  }
  u -= spinSec;
  if (u < settleSec) {
    const k = u / settleSec;
    const c = 0.5 - 0.5 * Math.cos(k * Math.PI);
    return start + direction * (turnsDeg + overDeg * (1 - c));
  }
  return start + direction * turnsDeg;
}

/** Wedge boundaries sit 22.5° off each center. The count is how many the arc crosses. */
export function boundariesCrossed(fromDeg: number, toDeg: number): number {
  const a = Math.floor((fromDeg + 22.5) / 45);
  const b = Math.floor((toDeg + 22.5) / 45);
  return Math.abs(b - a);
}

/** Winning wedge, screen space, once it is parked under the pointer. 0 is the start of the glow. */
export function wedgeGlow(sinceLand: number): number {
  if (sinceLand < 0) return 0;
  const u = sinceLand + 0.1;
  const amp = Math.min(1, u / 0.3) * (0.7 + 0.3 * Math.sin(u * 2 * Math.PI * 1.4 - Math.PI / 2)) * 0.85;
  return Math.max(0, Math.min(1, amp));
}

export interface SwipeRead {
  ok: boolean;
  direction: 1 | -1;
  speed: number;
  distance: number;
  extraTurns: number;
  /** A tap on the spin button, too short or too slow to spin. */
  nudge: boolean;
}

export function readSwipe(sample: {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  dtSec: number;
  centerX: number;
  centerY: number;
  radius: number;
  onButton: boolean;
}): SwipeRead {
  const dx = sample.x1 - sample.x0;
  const dy = sample.y1 - sample.y0;
  const distance = Math.hypot(dx, dy);
  const speed = sample.dtSec > 0 ? distance / sample.dtSec : 0;
  const offWheel = Math.hypot(sample.x0 - sample.centerX, sample.y0 - sample.centerY) > sample.radius;
  let direction: 1 | -1;
  if (offWheel) {
    direction = dx >= 0 ? 1 : -1;
  } else {
    const cross = (sample.x0 - sample.centerX) * dy - (sample.y0 - sample.centerY) * dx;
    direction = cross >= 0 ? 1 : -1;
  }
  const ok = distance >= SWIPE_MIN_PX && speed >= SWIPE_MIN_SPEED;
  const extraTurns = ok ? Math.min(1, Math.max(0, (speed - 800) / 1200)) * 3 : 0;
  return { ok, direction, speed, distance, extraTurns, nudge: !ok && sample.onButton };
}

export function localDayKey(now: number): string {
  const date = new Date(now);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function nextLocalMidnight(now: number): number {
  const date = new Date(now);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime();
}

export function emptyDailySpin(): Profile['dailySpin'] {
  return { day: '', used: false, banked: 0 };
}

export function ensureDailySpin(profile: Profile): void {
  const spin = profile.dailySpin;
  if (!spin || typeof spin.day !== 'string' || typeof spin.used !== 'boolean') {
    profile.dailySpin = emptyDailySpin();
    return;
  }
  const banked = (spin as { banked?: unknown }).banked;
  if (typeof banked !== 'number' || !Number.isInteger(banked) || banked < 0) spin.banked = 0;
}

export function spinAvailable(profile: Profile, now: number): boolean {
  ensureDailySpin(profile);
  if (!profile.dailySpin.used) return true;
  return profile.dailySpin.day !== localDayKey(now);
}

export function spinCountdownLabel(now: number): string {
  const ms = Math.max(0, nextLocalMidnight(now) - now);
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `Next spin in ${hours}h ${minutes}m`;
  if (minutes > 0) return `Next spin in ${minutes}m ${seconds}s`;
  return `Next spin in ${seconds}s`;
}

export function spinSubtitle(profile: Profile, now: number): string {
  return spinAvailable(profile, now) ? '1 free spin today' : spinCountdownLabel(now);
}

/** Countdown under the title while today's free spin is spent. Empty while it is still waiting. */
export function spinTitleNote(profile: Profile, now: number): string {
  return spinAvailable(profile, now) ? '' : spinCountdownLabel(now);
}

/** Ready label. A banked count is the extra spins still on the save. Cooldown with an empty bank offers packs. */
export const SPIN_BUTTON_READY = 'SWIPE TO SPIN!';
export const SPIN_BUTTON_BUY = 'Buy more spins!';

export function bankedSpins(profile: Profile): number {
  ensureDailySpin(profile);
  return profile.dailySpin.banked;
}

/** Free spin, or a spin bought from a pack. The free one is spent first. */
export function canSpin(profile: Profile, now: number): boolean {
  ensureDailySpin(profile);
  return spinAvailable(profile, now) || profile.dailySpin.banked > 0;
}

export function spinButtonLabel(profile: Profile, now: number): string {
  ensureDailySpin(profile);
  if (!canSpin(profile, now)) return SPIN_BUTTON_BUY;
  const banked = profile.dailySpin.banked;
  return banked > 0 ? `${SPIN_BUTTON_READY} (${banked})` : SPIN_BUTTON_READY;
}

/** Adds a purchased pack to the bank. The free daily spin is left for its own day. */
export function bankSpins(profile: Profile, count: number, now: number, productId: string): void {
  ensureDailySpin(profile);
  const spins = Math.max(0, Math.floor(count));
  if (spins === 0) return;
  profile.dailySpin.banked += spins;
  pushLedger(profile, now, 'purchase', {}, 0, productId);
}

export type SpinFanfare = 'common' | 'mid' | 'jackpot';

export interface SpinTrickGrant {
  id: string;
  name: string;
  count: number;
}

export interface SpinGrant {
  wedgeId: SpinWedgeId;
  index: number;
  acorns: number;
  honey: number;
  charmId: string | null;
  charmName: string | null;
  tricks: SpinTrickGrant[];
  /** `hour` shortens a running Hollow Log. `ready` means it was already open. */
  log: 'hour' | 'ready' | null;
  title: string;
  lines: string[];
  fanfare: SpinFanfare;
}

export function fanfareFor(id: SpinWedgeId): SpinFanfare {
  if (id === 'crown') return 'jackpot';
  if (id === 'acorns-40' || id === 'tricks') return 'common';
  return 'mid';
}

function pick<T>(rng: Rng, items: readonly T[]): T {
  return items[rng.int(items.length)] as T;
}

function ownedTricks(profile: Profile): { id: string; name: string }[] {
  const out: { id: string; name: string }[] = [];
  for (const [id, row] of Object.entries(profile.tricks)) {
    if (!row || row.level < 1) continue;
    const def = powerupById(id);
    if (!def || powerIsFree(def)) continue;
    out.push({ id, name: def.name });
  }
  if (out.length > 0) return out;
  const starter = powerupById('quill-burst');
  return [{ id: 'quill-burst', name: starter?.name ?? 'Quill Burst' }];
}

function rollWedge(rng: Rng): (typeof SPIN_WEDGES)[number] {
  let roll = rng.int(100);
  for (const wedge of SPIN_WEDGES) {
    if (roll < wedge.weight) return wedge;
    roll -= wedge.weight;
  }
  return SPIN_WEDGES[0];
}

/** The wedge, the charm, the tricks, and the mystery are all chosen before the wheel moves. */
export function rollSpin(profile: Profile, rng: Rng, now: number, wedgeId?: SpinWedgeId): SpinGrant {
  const wedge = SPIN_WEDGES.find((item) => item.id === wedgeId) ?? rollWedge(rng);
  const base: SpinGrant = {
    wedgeId: wedge.id,
    index: wedge.index,
    acorns: 0,
    honey: 0,
    charmId: null,
    charmName: null,
    tricks: [],
    log: null,
    title: wedge.odds,
    lines: [],
    fanfare: fanfareFor(wedge.id),
  };
  if (wedge.id === 'acorns-40') {
    base.acorns = 40;
    base.title = '40 acorns';
    base.lines = ['40 acorns'];
  } else if (wedge.id === 'acorns-150') {
    base.acorns = 150;
    base.title = '150 acorns';
    base.lines = ['150 acorns'];
  } else if (wedge.id === 'honey') {
    base.honey = 10;
    base.title = '10 honey';
    base.lines = ['10 honey'];
  } else if (wedge.id === 'tricks') {
    const trick = pick(rng, ownedTricks(profile));
    base.tricks = [{ ...trick, count: 2 }];
    base.title = '2 Tricks';
    base.lines = [`2 × ${trick.name}`];
  } else if (wedge.id === 'copper') {
    const charm = pick(rng, charmsOfTier('copper'));
    base.charmId = charm.id;
    base.charmName = charm.name;
    base.title = charm.name;
    base.lines = ['A Copper Charm'];
  } else if (wedge.id === 'log') {
    const ready = (profile.log?.readyAt ?? 0) <= now;
    base.log = ready ? 'ready' : 'hour';
    base.title = 'Prize Box';
    base.lines = [ready ? 'The Prize Box is already open.' : 'The Prize Box opens 1 hour sooner.'];
  } else if (wedge.id === 'mystery') {
    const which = rng.int(3);
    base.title = 'Mystery gift';
    if (which === 0) {
      base.acorns = 80;
      base.lines = ['80 acorns'];
    } else if (which === 1) {
      base.honey = 20;
      base.lines = ['20 honey'];
    } else {
      const trick = pick(rng, ownedTricks(profile));
      base.tricks = [{ ...trick, count: 3 }];
      base.lines = [`3 × ${trick.name}`];
    }
  } else {
    const charm = pick(rng, charmsOfTier('gold'));
    base.charmId = charm.id;
    base.charmName = charm.name;
    base.acorns = 500;
    base.title = 'Golden Crown';
    base.lines = [charm.name, '500 acorns'];
  }
  if (base.charmId && !base.charmName) base.charmName = charmById(base.charmId)?.name ?? null;
  return base;
}

/** Writes the wallet, the bag, and the spin charge. The free spin is spent before a banked one. */
export function grantSpin(profile: Profile, prize: SpinGrant, now: number): void {
  ensureDailySpin(profile);
  if (spinAvailable(profile, now)) {
    profile.dailySpin.day = localDayKey(now);
    profile.dailySpin.used = true;
  } else if (profile.dailySpin.banked > 0) {
    profile.dailySpin.banked -= 1;
  } else {
    profile.dailySpin.day = localDayKey(now);
    profile.dailySpin.used = true;
  }
  if (prize.acorns) profile.wallet.acorns += prize.acorns;
  if (prize.honey) profile.wallet.honey += prize.honey;
  if (prize.charmId) grantCharm(profile, prize.charmId, 1);
  for (const trick of prize.tricks) {
    if (!profile.tricks[trick.id]) profile.tricks[trick.id] = { level: 1 };
    addTrickStock(profile, trick.id, trick.count);
  }
  if (prize.log === 'hour') {
    if (!profile.log) profile.log = emptyLog();
    if (profile.log.readyAt > now) profile.log.readyAt = Math.max(now, profile.log.readyAt - 60 * 60 * 1000);
  }
  const currencies: { acorns?: number; honey?: number } = {};
  if (prize.acorns) currencies.acorns = prize.acorns;
  if (prize.honey) currencies.honey = prize.honey;
  pushLedger(profile, now, 'spin', currencies, 0, prize.wedgeId);
}
